import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { api } from '@/lib/api'
import { accountSettingsKeys } from './useAccountSettings'
import { propertyKeys } from './useProperties'
import { customerKeys } from './useCustomers'
import { leaseKeys } from './useLeases'
import { invoiceKeys } from './useInvoices'

export type SeedEntityType = 'properties' | 'units' | 'customers' | 'leases' | 'invoices'

interface SeedSampleResult {
  seeded: boolean
  reason?: string
  entities_created?: string[]
  /** Present on seeded:true responses; subset depends on which entities were created. */
  ids?: {
    property_id?: number
    unit_id?: number
    customer_id?: number
    lease_id?: number
    invoice_id?: number
  }
}

interface SeedSampleResponse {
  data: SeedSampleResult
}

/**
 * POST /api/{entity}/seed_sample
 *
 * On success (201 seeded: true):
 *  - invalidates accountSettingsKeys.all so per-entity counts refresh
 *  - invalidates the relevant list query so the new entity appears
 *
 * On 200 seeded: false (data already exists): no-op — data is still fresh.
 */
export function useSeedSample(entityType: SeedEntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SeedSampleResponse>(`/api/${entityType}/seed_sample`)
      return data.data
    },
    onSuccess: () => {
      // Always invalidate accountSettings so per-entity counts refresh and buttons hide.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })

      // Invalidate the relevant list query so newly created entities appear.
      switch (entityType) {
        case 'properties':
          // Property seed cascades to room_type + unit; invalidate BOTH lists
          // and details so the property card on PropertyListPage reflects the
          // new room/unit counts AND the PropertyDetailPage (if open) refreshes.
          queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
          queryClient.invalidateQueries({ queryKey: propertyKeys.details() })
          break
        case 'units':
          // Units live inside property detail; invalidate property details + lists.
          queryClient.invalidateQueries({ queryKey: propertyKeys.details() })
          queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
          break
        case 'customers':
          queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
          break
        case 'leases':
          queryClient.invalidateQueries({ queryKey: leaseKeys.lists() })
          break
        case 'invoices':
          queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
          break
      }
    },
  })
}

/** Invalidate every list touched by the rich demo seed / reset. */
function invalidateDemoData(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
  queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
  queryClient.invalidateQueries({ queryKey: propertyKeys.details() })
  queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
  queryClient.invalidateQueries({ queryKey: leaseKeys.lists() })
  queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
}

/**
 * POST /api/onboarding/seed_demo — the rich ~6-months-of-history "Explore with
 * sample data" seed (2 properties, 50 customers, 6 months of invoices/payments/
 * readings, leases, CMMS). Returns the same {seeded, ids} shape as useSeedSample.
 */
export function useSeedDemo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SeedSampleResponse>('/api/onboarding/seed_demo')
      return data.data
    },
    onSuccess: () => invalidateDemoData(queryClient),
  })
}

/** A row of the sample-reset plan: the own record and the sample one it uses. */
export interface SampleResetRecord {
  /** A model name such as Lease, Unit, Invoice, FixedAsset, DebtCase. */
  type: string
  id: number
  label: string
}

export interface SampleResetBlocker extends SampleResetRecord {
  depends_on: SampleResetRecord
}

/**
 * GET /api/onboarding/sample_data (EP-160 T160-101r, SampleReset#plan): the dry
 * run of the sample-only reset. `counts` is keyed by table name: the 17 tagged
 * tables are always present, another table (e.g. `debt_cases`) only when the
 * reset would delete rows there. `blockers` is capped at 50; `blocker_count` is
 * the full number.
 */
export interface SampleResetPlan {
  counts: Record<string, number>
  blockers: SampleResetBlocker[]
  blocker_count: number
  own_counts: { properties: number }
  needs_flowrent: boolean
}

export interface SampleResetResult {
  reset: boolean
  counts: Record<string, number>
  own_counts: { properties: number }
}

/** Why the server refused the reset (422), as the dialog shows it. */
export type SampleResetRefusal =
  | { code: 'sample_reset_blocked'; blockers: SampleResetBlocker[]; blocker_count: number }
  | { code: 'sample_reset_needs_flowrent' }

export const sampleResetKeys = {
  all: ['sampleReset'] as const,
  plan: () => [...sampleResetKeys.all, 'plan'] as const,
}

/**
 * The reset plan, fetched only while the delete dialog is open. gcTime 0 drops
 * it when the dialog closes, so every opening shows a fresh count.
 */
export function useSampleResetPlan(enabled: boolean) {
  return useQuery({
    queryKey: sampleResetKeys.plan(),
    queryFn: async () => {
      const { data } = await api.get<{ data: SampleResetPlan }>('/api/onboarding/sample_data')
      return data.data
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  })
}

interface SampleResetErrorBody {
  error?: {
    code?: string
    details?: { blockers?: SampleResetBlocker[]; blocker_count?: number }
  }
}

/** The refusal carried by a 422 from DELETE /api/onboarding/sample_data, else null. */
export function sampleResetRefusalFromError(error: unknown): SampleResetRefusal | null {
  if (!isAxiosError<SampleResetErrorBody>(error) || error.response?.status !== 422) return null
  const body = error.response.data?.error
  if (body?.code === 'sample_reset_needs_flowrent') return { code: 'sample_reset_needs_flowrent' }
  if (body?.code === 'sample_reset_blocked') {
    const blockers = body.details?.blockers ?? []
    return { code: 'sample_reset_blocked', blockers, blocker_count: body.details?.blocker_count ?? blockers.length }
  }
  return null
}

/**
 * DELETE /api/onboarding/sample_data: deletes the sample data and nothing else
 * (SampleReset). The account's own records stay; `own_counts.properties` says
 * how many own buildings are left. A 422 refusal reads through
 * sampleResetRefusalFromError.
 */
export function useResetSampleData() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<{ data: SampleResetResult }>('/api/onboarding/sample_data')
      return data.data
    },
    onSuccess: () => {
      invalidateDemoData(queryClient)
      // A reset that keeps the owner's own buildings lands on the dashboard.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.removeQueries({ queryKey: sampleResetKeys.all })
    },
  })
}
